import React from "react";
import { X, Scale } from "lucide-react";

/**
 * Bottom-of-homepage block listing partner orgs that help with legal issues.
 * Each partner has either a "Plus d'infos" popup or a direct link.
 * Driven by site_config.legal_help — fully editable from admin.
 */
export default function LegalHelpBlock({ data }) {
  const [openPartner, setOpenPartner] = React.useState(null);
  if (!data || data.enabled === false) return null;
  const partners = data.partners || [];
  if (partners.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="legal-help-block">
      <div className="neo-card p-6 sm:p-10 bg-cef-paper border-2 border-black">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-10 h-10 inline-flex items-center justify-center bg-cef-yellow border-2 border-black rounded-md"><Scale className="w-5 h-5" strokeWidth={2.5} /></span>
          <span className="chip bg-black text-white">Aide juridique gratuite</span>
        </div>
        <h2 className="font-display font-black text-2xl sm:text-4xl tracking-tighter leading-tight" data-testid="legal-help-title">{data.title}</h2>
        {data.subtitle && <p className="mt-3 text-neutral-700 max-w-3xl" data-testid="legal-help-subtitle">{data.subtitle}</p>}

        <div className="mt-7 grid sm:grid-cols-2 gap-5">
          {partners.map((p, idx) => (
            <div key={`${p.short_name || p.name}-${idx}`} className="neo-card p-5 bg-white flex flex-col" data-testid={`legal-partner-${idx}`}>
              <div className="flex items-center gap-4">
                {p.logo_url && (
                  <a href={p.url || "#"} target="_blank" rel="noreferrer" className="shrink-0" aria-label={p.name}>
                    <img src={p.logo_url} alt={p.name} className="w-20 h-20 object-contain" loading="lazy" data-testid={`legal-partner-${idx}-logo`} />
                  </a>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black text-lg tracking-tight leading-tight">{p.name}</div>
                  {p.short_name && p.short_name !== p.name && <div className="text-xs uppercase tracking-widest text-neutral-500">{p.short_name}</div>}
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-cef-blue underline truncate block mt-1">{p.url.replace(/^https?:\/\//, "")}</a>}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.popup_enabled && p.popup_content ? (
                  <button type="button" onClick={() => setOpenPartner(p)} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`legal-partner-${idx}-popup-btn`}>
                    {p.button_label || "Plus d'infos"}
                  </button>
                ) : null}
                {p.url && (
                  <a href={p.url} target="_blank" rel="noreferrer" className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid={`legal-partner-${idx}-visit`}>
                    Visiter le site ↗
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {openPartner && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpenPartner(null)} data-testid="legal-popup">
          <div onClick={(e) => e.stopPropagation()} className="neo-card bg-white p-6 w-full max-w-lg my-12">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                {openPartner.logo_url && <img src={openPartner.logo_url} alt={openPartner.name} className="w-14 h-14 object-contain" />}
                <div>
                  <h3 className="font-display font-black text-xl tracking-tighter leading-tight">{openPartner.name}</h3>
                  {openPartner.url && <a href={openPartner.url} target="_blank" rel="noreferrer" className="text-xs text-cef-blue underline">{openPartner.url.replace(/^https?:\/\//, "")}</a>}
                </div>
              </div>
              <button type="button" onClick={() => setOpenPartner(null)} className="w-8 h-8 inline-flex items-center justify-center border-2 border-black rounded-md hover:bg-cef-yellow shrink-0" aria-label="Fermer" data-testid="legal-popup-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm whitespace-pre-line leading-relaxed" data-testid="legal-popup-content">{openPartner.popup_content}</div>
            {openPartner.url && (
              <a href={openPartner.url} target="_blank" rel="noreferrer" className="neo-btn bg-cef-pink text-white mt-5" data-testid="legal-popup-visit">
                Visiter le site ↗
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
